import { useState } from "react";
import { Check } from "lucide-react";
import { Btn } from "../../components/ui/Btn";
import { FieldLabel, Modal, inputStyle } from "../../components/ui/Modal";
import { F, T, CORAL } from "../../theme/tokens";
import { FIELD_TYPES, FormField, FieldType } from "./types";

const HAS_PLACEHOLDER: FieldType[] = ["text", "name", "date", "dropdown"];

function Segmented<V extends string>({ value, options, onChange }: { value: V; options: { value: V; label: string }[]; onChange: (v: V) => void }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${options.length}, 1fr)`, gap: 10 }}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)}
            style={{ padding: "11px 8px", borderRadius: 8, fontFamily: F, fontSize: 13, fontWeight: 500, cursor: "pointer", border: `1px solid ${on ? CORAL : T.border}`, background: on ? "#fdf0f4" : T.s2, color: on ? CORAL : T.sub }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Add or edit one field. Label is required; the rest of the form follows the chosen type. */
export function FieldModal({ initial, isNew, onSave, onClose }: {
  initial: FormField; isNew: boolean; onSave: (f: FormField) => void; onClose: () => void;
}) {
  const [f, setF] = useState<FormField>(initial);
  const [optionsText, setOptionsText] = useState((initial.options ?? []).join(","));
  const set = <K extends keyof FormField>(k: K, v: FormField[K]) => setF((p) => ({ ...p, [k]: v }));

  const options = optionsText.split(",").map((o) => o.trim()).filter(Boolean);
  const dupes = new Set(options.map((o) => o.toLowerCase())).size !== options.length;
  const optionsError = f.type === "dropdown" && (options.length < 2 ? "Add at least two options." : dupes ? "Options must be different." : null);
  const valid = f.label.trim().length > 0 && !optionsError;

  function save() {
    if (!valid) return;
    const out: FormField = { ...f, label: f.label.trim(), helpText: f.helpText.trim() };
    if (HAS_PLACEHOLDER.includes(f.type)) out.placeholder = (f.placeholder ?? "").trim(); else delete out.placeholder;
    if (f.type === "dropdown") out.options = options; else delete out.options;
    if (f.type === "id_document") out.idSides = f.idSides ?? "both"; else delete out.idSides;
    if (f.type === "file_upload" || f.type === "image_upload") out.fileMode = f.fileMode ?? "single"; else delete out.fileMode;
    onSave(out);
  }

  return (
    <Modal title={isNew ? "Add Field" : "Edit Field"} onClose={onClose}
      footer={<>
        <Btn variant="ghost" size="md" onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" size="md" disabled={!valid} onClick={save}><Check size={13} /> Save Field</Btn>
      </>}>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <FieldLabel required>Field label</FieldLabel>
          <input value={f.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. Full Name" style={inputStyle} autoFocus />
        </div>

        <div>
          <FieldLabel required>Field type</FieldLabel>
          <select value={f.type} onChange={(e) => set("type", e.target.value as FieldType)} style={{ ...inputStyle, cursor: "pointer" }}>
            {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div>
          <FieldLabel>Status</FieldLabel>
          <Segmented value={f.required ? "required" : "optional"} onChange={(v) => set("required", v === "required")}
            options={[{ value: "required", label: "Required" }, { value: "optional", label: "Optional" }]} />
        </div>

        {f.type === "dropdown" && (
          <div>
            <FieldLabel hint="Comma-separated list of options">Options</FieldLabel>
            <textarea value={optionsText} onChange={(e) => setOptionsText(e.target.value)} rows={3} placeholder="Option 1, Option 2, Option 3"
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} />
            {optionsError && <p style={{ fontSize: 11, color: "#991b1b", fontFamily: F, margin: "6px 0 0" }}>{optionsError}</p>}
          </div>
        )}

        {f.type === "id_document" && (
          <div>
            <FieldLabel>Sides to collect</FieldLabel>
            <Segmented value={f.idSides ?? "both"} onChange={(v) => set("idSides", v)}
              options={[{ value: "front", label: "Front" }, { value: "back", label: "Back" }, { value: "both", label: "Front + Back" }]} />
          </div>
        )}

        {(f.type === "file_upload" || f.type === "image_upload") && (
          <div>
            <FieldLabel>Files</FieldLabel>
            <Segmented value={f.fileMode ?? "single"} onChange={(v) => set("fileMode", v)}
              options={[{ value: "single", label: "Single file" }, { value: "multiple", label: "Multiple files" }]} />
          </div>
        )}

        {HAS_PLACEHOLDER.includes(f.type) && (
          <div>
            <FieldLabel>Placeholder</FieldLabel>
            <input value={f.placeholder ?? ""} onChange={(e) => set("placeholder", e.target.value)} placeholder="Enter placeholder text…" style={inputStyle} />
          </div>
        )}

        <div>
          <FieldLabel hint="Shown below the field as guidance for the user">Help text</FieldLabel>
          <input value={f.helpText} onChange={(e) => set("helpText", e.target.value)} placeholder="Optional guidance…" style={inputStyle} />
        </div>
      </div>
    </Modal>
  );
}
